import React, { useEffect, useState } from "react";
import { api, money, categoryColor } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Play, Square, Clock, MapPin, Loader2, Timer, Sunrise, Sunset, Coffee, Building2, MessageSquarePlus, X, CheckCircle2, XCircle, AlertTriangle, Lock } from "lucide-react";

const ACTIVE = ["to_schedule", "to_do", "estimate_approved", "purchasing", "in_progress", "waiting_info", "job_done"];

const PROP_PALETTE = [
  { bg: "bg-rose-100", text: "text-rose-700", dot: "#e11d48" },
  { bg: "bg-amber-100", text: "text-amber-700", dot: "#d97706" },
  { bg: "bg-emerald-100", text: "text-emerald-700", dot: "#059669" },
  { bg: "bg-sky-100", text: "text-sky-700", dot: "#0284c7" },
  { bg: "bg-violet-100", text: "text-violet-700", dot: "#7c3aed" },
  { bg: "bg-teal-100", text: "text-teal-700", dot: "#0d9488" },
  { bg: "bg-fuchsia-100", text: "text-fuchsia-700", dot: "#c026d3" },
  { bg: "bg-orange-100", text: "text-orange-700", dot: "#ea580c" },
];
export const propColor = (name) => {
  const s = String(name || "—");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PROP_PALETTE[h % PROP_PALETTE.length];
};

const getPosition = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve(null);
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
    () => resolve(null), { enableHighAccuracy: true, timeout: 8000 });
});

const ReminderBanner = ({ summary }) => {
  const { t } = useLang();
  if (!summary) return null;
  const hour = new Date().getHours();
  if (summary.forgotten_shift) {
    return (
      <div data-testid="forgotten-shift-banner" className="rounded-2xl border-2 border-red-300 bg-red-50 p-4 flex items-start gap-3">
        <AlertTriangle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
        <div>
          <div className="font-bold text-red-800">{t("forgotCheckoutTitle")}</div>
          <div className="text-sm text-red-700 mt-0.5">{t("forgottenShiftMsg")} ({summary.forgotten_shift.date})</div>
        </div>
      </div>
    );
  }
  if (summary.open && hour >= 18) {
    return (
      <div data-testid="late-checkout-banner" className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
        <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <div className="font-bold text-amber-800">{t("forgotCheckoutTitle")}</div>
          <div className="text-sm text-amber-700 mt-0.5">
            {t("lateShiftMsg")}
            {summary.open_job ? ` ${t("lateJobMsg")}: ${summary.open_job.number} — ${summary.open_job.title}` : ""}
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const JobCard = ({ tk, userId, hasOpenElsewhere, shiftOpen, onReload }) => {
  const { t, lang } = useLang();
  const locale = lang === "en" ? "en-US" : "pt-BR";
  const [busy, setBusy] = useState(false);
  const myEntries = (tk.time_entries || []).filter((e) => e.tech_id === userId);
  const open = myEntries.find((e) => !e.clock_out);
  const todayKey = new Date().toISOString().slice(0, 10);
  const todayHours = myEntries.filter((e) => (e.clock_in || "").slice(0, 10) === todayKey).reduce((s, e) => s + (e.hours || 0), 0);
  const pc = propColor(tk.unit_name);

  const punch = async (action) => {
    setBusy(true);
    try {
      const loc = await getPosition();
      await api.post(`/tickets/${tk.id}/${action}`, loc || {});
      toast.success(action === "clock-in" ? t("checkIn") : t("checkOut"));
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };
  const fmt = (iso) => new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });

  return (
    <div data-testid={`clock-job-${tk.id}`} className={`rounded-2xl border-2 p-5 transition-colors ${open ? "border-emerald-400 bg-emerald-50/40" : "border-slate-200 bg-white"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${categoryColor(tk.category)}`}>{t(tk.category)}</span>
            <span className="font-mono text-xs text-slate-400">{tk.number}</span>
          </div>
          <h3 className="font-semibold text-slate-900 mt-1 truncate">{tk.title}</h3>
          <span data-testid={`prop-pill-${tk.id}`} className={`inline-flex items-center gap-1.5 mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${pc.bg} ${pc.text}`}>
            <Building2 className="w-3 h-3" />{tk.unit_name || "—"}
          </span>
        </div>
        {open && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 shrink-0"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />{t("onSite")}</span>}
      </div>

      <div className="mt-3 flex items-center gap-4 text-sm">
        <span className="inline-flex items-center gap-1 text-slate-500"><Timer className="w-4 h-4" />{t("todayHours")}: <b className="text-slate-800">{todayHours.toFixed(2)}h</b></span>
        {open && <span className="inline-flex items-center gap-1 text-emerald-700"><Clock className="w-4 h-4" />{fmt(open.clock_in)}</span>}
      </div>

      {(tk.tenant_name || tk.tenant_contact || tk.access_code) && (
        <div data-testid={`tenant-info-${tk.id}`} className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-xs space-y-0.5">
          {tk.tenant_name && <div className="text-slate-700"><span className="font-bold text-slate-500 uppercase text-[10px]">{t("tenantName")}:</span> {tk.tenant_name}</div>}
          {tk.tenant_contact && <div className="text-slate-700"><span className="font-bold text-slate-500 uppercase text-[10px]">{t("tenantContact")}:</span> <a href={`tel:${tk.tenant_contact}`} className="text-blue-600">{tk.tenant_contact}</a></div>}
          {tk.access_code && <div className="text-slate-700"><span className="font-bold text-slate-500 uppercase text-[10px]">{t("accessCode")}:</span> <code className="font-mono bg-amber-50 text-amber-800 px-1 rounded">{tk.access_code}</code></div>}
        </div>
      )}

      <button data-testid={`punch-${tk.id}`} disabled={busy || (!open && (hasOpenElsewhere || !shiftOpen))} onClick={() => punch(open ? "clock-out" : "clock-in")}
        title={!open && !shiftOpen ? t("openShiftFirst") : !open && hasOpenElsewhere ? t("closeOtherJobFirst") : ""}
        className={`mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl py-3.5 text-base font-bold text-white transition-colors disabled:opacity-40 ${open ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : open ? <Square className="w-5 h-5" /> : !shiftOpen ? <Lock className="w-5 h-5" /> : <Play className="w-5 h-5" />}
        {open ? t("checkOut") : t("checkIn")}
      </button>
      {!open && !shiftOpen && <p data-testid={`shift-locked-hint-${tk.id}`} className="text-[11px] text-slate-500 mt-1.5 text-center inline-flex items-center gap-1 w-full justify-center"><Lock className="w-3 h-3" />{t("openShiftFirstHint")}</p>}
      {!open && shiftOpen && hasOpenElsewhere && <p className="text-[11px] text-amber-600 mt-1.5 text-center">{t("switchJobHint")}</p>}

      {myEntries.length > 0 && (
        <div className="mt-3 space-y-1 border-t border-slate-100 pt-2">
          {myEntries.slice(-4).reverse().map((e) => (
            <div key={e.id} className="flex items-center gap-2 text-xs text-slate-500">
              <Clock className="w-3 h-3" />
              <span className="flex-1">{fmt(e.clock_in)} → {e.clock_out ? fmt(e.clock_out) : <span className="text-emerald-600">{t("running")}</span>}</span>
              {(e.clock_in_loc || e.clock_out_loc) && <a href={`https://maps.google.com/?q=${(e.clock_out_loc || e.clock_in_loc).lat},${(e.clock_out_loc || e.clock_in_loc).lng}`} target="_blank" rel="noreferrer" className="text-blue-500"><MapPin className="w-3 h-3" /></a>}
              <span className="tabular-nums">{(e.hours || 0).toFixed(2)}h</span>
              <span className="tabular-nums text-slate-400">{money(e.cost)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ShiftHeader = ({ summary, onReload }) => {
  const { t, lang } = useLang();
  const locale = lang === "en" ? "en-US" : "pt-BR";
  const [busy, setBusy] = useState(false);
  const shift = summary?.shift;
  const open = summary?.open;

  const punch = async (action) => {
    setBusy(true);
    try {
      const loc = await getPosition();
      await api.post(`/shifts/${action}`, loc || {});
      toast.success(action === "start" ? t("startDay") : t("endDay"));
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };
  const fmt = (iso) => iso ? new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }) : "—";

  const Stat = ({ label, value, cls }) => (
    <div className="text-center">
      <div className="text-[10px] uppercase font-bold text-slate-400">{label}</div>
      <div className={`text-xl font-bold tabular-nums ${cls || "text-slate-900"}`}>{value}</div>
    </div>
  );

  return (
    <div data-testid="shift-header" className={`rounded-2xl border-2 p-5 ${open ? "border-emerald-400 bg-emerald-50/50" : "border-slate-200 bg-white"}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className={`w-11 h-11 rounded-full flex items-center justify-center ${open ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
            {open ? <Sunrise className="w-5 h-5" /> : <Sunset className="w-5 h-5" />}
          </span>
          <div>
            <div className="font-semibold text-slate-900">{open ? t("onShift") : t("dayNotStarted")}</div>
            <div className="text-xs text-slate-500">{shift ? `${t("startDay")}: ${fmt(shift.clock_in)}${shift.clock_out ? ` · ${t("endDay")}: ${fmt(shift.clock_out)}` : ""}` : t("startDayHint")}</div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <Stat label={t("shiftHours")} value={`${(summary?.shift_hours ?? 0).toFixed(2)}h`} />
          <Stat label={t("jobHours")} value={`${(summary?.job_hours ?? 0).toFixed(2)}h`} cls="text-emerald-700" />
          <Stat label={t("officeHours")} value={`${(summary?.standby_hours ?? 0).toFixed(2)}h`} cls="text-indigo-600" />
          {summary?.lunch_deduction > 0 && (
            <div className="text-center" title={t("lunchDeduction")}>
              <div className="text-[10px] uppercase font-bold text-slate-400 flex items-center gap-1 justify-center"><Coffee className="w-3 h-3" />{t("lunchDeduction")}</div>
              <div className="text-xl font-bold text-slate-500 tabular-nums">−{summary.lunch_deduction}min</div>
            </div>
          )}
        </div>
        <button data-testid="shift-punch" disabled={busy} onClick={() => punch(open ? "end" : "start")}
          className={`inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-base font-bold text-white transition-colors disabled:opacity-60 ${open ? "bg-slate-800 hover:bg-slate-900" : "bg-emerald-600 hover:bg-emerald-700"}`}>
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : open ? <Sunset className="w-5 h-5" /> : <Sunrise className="w-5 h-5" />}
          {open ? t("endDay") : t("startDay")}
        </button>
      </div>
    </div>
  );
};

const RequestsPanel = ({ requests, onReload }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.message.trim()) return toast.error(t("requestMsgRequired"));
    setBusy(true);
    try {
      await api.post("/time-requests", form);
      toast.success(t("requestSent"));
      setOpen(false); setForm({ date: new Date().toISOString().slice(0, 10), message: "" });
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };

  const badge = (s) => s === "approved" ? "bg-emerald-50 text-emerald-700" : s === "rejected" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700";

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
        <h2 className="text-base font-semibold text-slate-900">{t("timeRequests")}</h2>
        <button data-testid="new-request-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold px-3 py-2 hover:bg-slate-800">
          <MessageSquarePlus className="w-4 h-4" />{t("requestCorrection")}
        </button>
      </div>
      <div className="divide-y divide-slate-50">
        {requests.length === 0 && <div className="px-5 py-8 text-center text-slate-400 text-sm">{t("noRequests")}</div>}
        {requests.map((r) => (
          <div key={r.id} data-testid={`request-${r.id}`} className="px-5 py-3 flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-800">{r.message}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">{r.date}{r.resolution ? ` · ${r.resolution}` : ""}</div>
            </div>
            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge(r.status)}`}>{t(r.status)}</span>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">{t("requestCorrection")}</h2>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("date")}</label>
                <input data-testid="request-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("requestMessage")}</label>
                <textarea data-testid="request-message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t("requestPlaceholder")} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
              <button data-testid="request-submit" disabled={busy} onClick={submit} className="rounded-lg px-4 py-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60">{t("send")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function TimeClock() {
  const { t } = useLang();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState(null);
  const [requests, setRequests] = useState([]);

  const load = () => {
    api.get("/tickets").then(setTickets).catch(() => {});
    api.get("/projects/assigned/mine").then(setProjects).catch(() => setProjects([]));
    api.get("/shifts/today").then(setSummary).catch(() => {});
    api.get("/time-requests").then(setRequests).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const myJobs = tickets.filter((tk) => {
    const mine = user.role === "technician"
      ? (tk.assigned_to === user.id || (tk.assigned_techs || []).some((x) => x.id === user.id))
      : (tk.assigned_to || (tk.assigned_techs || []).length > 0);
    return mine && ACTIVE.includes(tk.status);
  });
  const hasOpenTicket = myJobs.some((tk) => (tk.time_entries || []).some((e) => e.tech_id === user.id && !e.clock_out));
  const hasOpenProject = projects.some((p) => (p.time_entries || []).some((e) => e.tech_id === user.id && !e.clock_out));
  const hasOpenElsewhere = hasOpenTicket || hasOpenProject;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("timeClock")}</h1>
        <p className="text-slate-500 text-sm mt-1">{t("myJobs")}</p>
      </div>

      <ReminderBanner summary={summary} />

      <ShiftHeader summary={summary} onReload={load} />

      {/* Service Calls */}
      {myJobs.length > 0 && (
        <div>
          <div className="text-xs font-bold uppercase text-slate-500 tracking-widest mb-2">Service Calls</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {myJobs.map((tk) => <JobCard key={tk.id} tk={tk} userId={user.id} hasOpenElsewhere={hasOpenElsewhere} shiftOpen={!!summary?.open} onReload={load} />)}
          </div>
        </div>
      )}

      {/* Projects */}
      {projects.length > 0 && (
        <div>
          <div className="text-xs font-bold uppercase text-slate-500 tracking-widest mb-2">Projects</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((pr) => (
              <ProjectClockCard key={pr.id} project={pr} userId={user.id}
                hasOpenElsewhere={hasOpenElsewhere} shiftOpen={!!summary?.open} onReload={load} />
            ))}
          </div>
        </div>
      )}

      {myJobs.length === 0 && projects.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-400">
          <Timer className="w-10 h-10 mx-auto mb-2 text-slate-300" />{t("noJobs")}
        </div>
      )}

      <RequestsPanel requests={requests} onReload={load} />
    </div>
  );
}

function ProjectClockCard({ project, userId, hasOpenElsewhere, shiftOpen, onReload }) {
  const [busy, setBusy] = useState(false);
  const open = (project.time_entries || []).find((e) => e.tech_id === userId && !e.clock_out);
  const punch = async (action) => {
    setBusy(true);
    try {
      const loc = await getPosition();
      await api.post(`/projects/${project.id}/${action}`, loc || {});
      toast.success(action === "clock-in" ? "Check-in do projeto" : "Check-out do projeto");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  const cost = ((project.totals && project.totals.contract_effective) || project.contract_value || 0);
  const progress = (project.totals && project.totals.progress_pct) || 0;

  return (
    <div className={`bg-white border-2 rounded-xl p-4 space-y-3 ${open ? "border-emerald-400 ring-2 ring-emerald-100" : "border-slate-200"}`}
         data-testid={`prj-clock-card-${project.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] font-mono text-slate-400">{project.number}</div>
          <div className="text-sm font-bold text-slate-900 truncate">{project.name}</div>
          <div className="text-[11px] text-slate-500 truncate">{project.client_name} · {project.property_name || "—"}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[9px] uppercase text-slate-400 font-bold">Contract</div>
          <div className="text-sm font-bold tabular-nums text-slate-900">{money(cost)}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 text-[11px] text-slate-500">
        <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500" style={{ width: `${progress}%` }} />
        </div>
        <span className="tabular-nums w-8 text-right font-semibold">{progress}%</span>
      </div>
      <button
        data-testid={`prj-clock-punch-${project.id}`}
        disabled={busy || (!open && (hasOpenElsewhere || !shiftOpen))}
        onClick={() => punch(open ? "clock-out" : "clock-in")}
        className={`w-full inline-flex items-center justify-center gap-2 rounded-lg font-bold px-3 py-2.5 text-sm ${
          open ? "bg-red-500 hover:bg-red-600 text-white"
               : "bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
        }`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : open ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        {open ? `Check-out (aberto ${open.clock_in?.slice(11, 16)})` : (!shiftOpen ? "Bata ponto do turno primeiro" : hasOpenElsewhere ? "Feche outro job primeiro" : "Check-in")}
      </button>
    </div>
  );
}
